import React, { useState } from 'react';
import {
  Pressable,
  StyleSheet,
  // eslint-disable-next-line no-restricted-imports
  Text,
  TextStyle,
  ViewStyle,
} from 'react-native';
import { ButtonComponentAnalytics } from 'shared/analytics/types/componentEvents';
import { ButtonTypes } from 'shared/analytics/types/componentTypes';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import { useComponentAnalytics } from 'shared/hooks/analytics/useComponentAnalytics';
import {
  PressableAnalyticsProps,
  usePressableAnalytics,
} from 'shared/hooks/analytics/usePressableAnalytics';
import theme from 'shared/styles/theme';
import { VaroFontNames } from 'shared/styles/typography';
import { Maybe } from 'shared/utils/types';

export type ButtonType = 'PRIMARY' | 'SECONDARY' | 'SECONDARY_ALT' | 'TEXT';
export type ButtonSize = 'SMALL' | 'LARGE';
export type ButtonWidth = 'HUG' | 'FULL';

export interface ButtonProps extends PressableAnalyticsProps {
  label: string;
  key?: string;
  isDisabled?: boolean;
  onDark?: boolean;
  onPress?: Maybe<(...args: unknown[]) => unknown>;
  size?: ButtonSize;
  type?: ButtonType;
  width?: ButtonWidth;
  isLoading?: boolean;
  testID?: string;
  /** Fires a `Button Pressed` component analytics event on press. */
  componentAnalytics?: ButtonComponentAnalytics;
}

interface ComposableStyle {
  button?: ViewStyle;
  copy?: TextStyle;
  focus?: ViewStyle;
  hover?: ViewStyle;
  press?: ViewStyle;
  loading?: string;
  animation?: string;
}

interface ComposableStyles {
  PRIMARY: ComposableStyle;
  SECONDARY: ComposableStyle;
  SECONDARY_ALT: ComposableStyle;
  TEXT: ComposableStyle;
}

const getAnimation = (
  buttonType: ButtonType,
  buttonOnDark: ButtonProps['onDark'],
) => {
  switch (buttonType) {
    case 'PRIMARY':
      if (buttonOnDark) {
        return theme.color.text.primary;
      }
      return theme.color.text.inverse;
    case 'SECONDARY':
      if (buttonOnDark) {
        return theme.color.text.inverse;
      }
      return theme.color.interactive.primary;
    case 'SECONDARY_ALT':
      if (buttonOnDark) {
        return theme.color.text.inverse;
      }
      return theme.color.text.primary;
    case 'TEXT':
      if (buttonOnDark) {
        return theme.color.text.inverse;
      }
      return theme.color.interactive.primary;
    default:
      return theme.color.text.inverse;
  }
};

const composeButtonStyles = (
  buttonType: ButtonType,
  buttonIsDisabled: ButtonProps['isDisabled'],
  buttonOnDark: ButtonProps['onDark'],
): ComposableStyle => {
  if (buttonIsDisabled) {
    if (buttonOnDark) {
      return {
        button: disabledOnDarkStyles[buttonType].button,
        copy: disabledOnDarkStyles[buttonType].copy,
      };
    }

    return {
      button: disabledStyles[buttonType].button,
      copy: disabledStyles[buttonType].copy,
    };
  } else if (buttonOnDark) {
    return {
      button: onDarkStyles[buttonType].button,
      copy: onDarkStyles[buttonType].copy,
      focus: onDarkStyles[buttonType].focus,
      hover: onDarkStyles[buttonType].hover,
      press: onDarkStyles[buttonType].press,
    };
  }

  return {
    button: styles[buttonType].button,
    copy: styles[buttonType].copy,
    focus: styles[buttonType].focus,
    hover: styles[buttonType].hover,
    press: styles[buttonType].press,
  };
};

const Button = ({
  label,
  isDisabled = false,
  onDark = false,
  onPress,
  size = 'LARGE',
  type = 'PRIMARY',
  width = 'FULL',
  isLoading = false,
  analyticsId = '',
  customAttributes = null,
  componentAnalytics,
  testID,
}: ButtonProps) => {
  const buttonAnalytics = usePressableAnalytics({
    analyticsId,
    customAttributes,
  });
  const { logPressableClicked } = useComponentAnalytics();

  const computedStyles = composeButtonStyles(type, isDisabled, onDark);

  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);

  const handlePress = () => {
    buttonAnalytics?.();

    if (componentAnalytics) {
      logPressableClicked({
        pressable_name: componentAnalytics.pressableName,
        component_type: ButtonTypes[type],
        category: componentAnalytics.category,
        sub_category: componentAnalytics.subCategory,
      });
    }

    onPress?.();
  };

  return (
    <Pressable
      style={[
        baseStyles.button,
        width === 'HUG' ? baseStyles.hug : baseStyles.full,
        size === 'SMALL' ? baseStyles.small : baseStyles.large,
        computedStyles.button,
        isFocused && computedStyles.focus,
        isHovered && computedStyles.hover,
        isPressed && computedStyles.press,
      ]}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      onBlur={() => setIsFocused(false)}
      onFocus={() => setIsFocused(true)}
      onPressIn={() => setIsPressed(true)}
      onPressOut={() => setIsPressed(false)}
      onPress={handlePress}
      disabled={isDisabled || isLoading}
      aria-disabled={isDisabled || isLoading}
      aria-busy={isLoading}
      aria-label={label}
      role="button"
      testID={testID ?? label}
      accessibilityLabel={label}
    >
      {isLoading ? (
        <LoadingSpinner
          aria-label="button loading indicator"
          fill={getAnimation(type, onDark)}
        />
      ) : (
        <Text numberOfLines={1} style={[baseStyles.copy, computedStyles.copy]}>
          {label}
        </Text>
      )}
    </Pressable>
  );
};

const styles: ComposableStyles = {
  PRIMARY: StyleSheet.create({
    button: {
      backgroundColor: theme.color.interactive.primary,
    },
    copy: {
      color: theme.color.text.inverse,
    },
    focus: {
      backgroundColor: theme.color.interactive.primaryPressed,
      borderColor: theme.color.border.focused,
      borderWidth: 1,
    },
    hover: {
      backgroundColor: theme.color.interactive.primaryHovered,
    },
    press: {
      backgroundColor: theme.color.interactive.primaryPressed,
    },
  }),
  SECONDARY: StyleSheet.create({
    button: {
      backgroundColor: 'transparent',
      borderColor: theme.color.interactive.primary,
      borderWidth: 1,
    },
    copy: {
      color: theme.color.interactive.primary,
    },
    focus: {
      borderColor: theme.color.border.focused,
    },
    hover: {
      borderColor: theme.color.interactive.primaryHovered,
    },
    press: {
      borderColor: theme.color.interactive.primaryPressed,
    },
  }),
  SECONDARY_ALT: StyleSheet.create({
    button: {
      backgroundColor: 'transparent',
      borderColor: theme.color.interactive.black,
      borderWidth: 1,
    },
    copy: {
      color: theme.color.interactive.black,
    },
    focus: {
      borderColor: theme.color.border.focused,
    },
    hover: {
      borderColor: theme.color.interactive.blackHovered,
    },
    press: {
      borderColor: theme.color.interactive.blackPressed,
    },
  }),
  TEXT: StyleSheet.create({
    button: {
      backgroundColor: 'transparent',
      height: 'auto',
      paddingVertical: 0,
    },
    copy: {
      color: theme.color.interactive.primary,
    },
    focus: {
      borderColor: theme.color.border.focused,
      borderWidth: 1,
    },
  }),
};

const disabledStyles: ComposableStyles = {
  PRIMARY: StyleSheet.create({
    button: {
      backgroundColor: theme.color.interactive.disabled.background,
    },
    copy: {
      color: theme.color.interactive.disabled.text,
    },
  }),
  SECONDARY: StyleSheet.create({
    button: {
      borderColor: theme.color.interactive.disabled.border,
      borderWidth: 1,
    },
    copy: {
      color: theme.color.interactive.disabled.text,
    },
  }),
  SECONDARY_ALT: StyleSheet.create({
    button: {
      borderColor: theme.color.interactive.disabled.border,
      borderWidth: 1,
    },
    copy: {
      color: theme.color.interactive.disabled.text,
    },
  }),
  TEXT: StyleSheet.create({
    copy: {
      color: theme.color.interactive.disabled.text,
    },
  }),
};

const secondaryOnDarkSharedStyles = {
  button: {
    backgroundColor: 'transparent',
    borderColor: theme.color.interactive.inverse,
    borderWidth: 1,
  },
  copy: {
    color: theme.color.text.inverse,
  },
  focus: {
    borderColor: theme.color.interactive.inversePressed,
  },
  hover: {
    borderColor: theme.color.interactive.inverseHovered,
  },
  press: {
    borderColor: theme.color.interactive.inversePressed,
  },
};

const secondaryDisabledOnDarkSharedStyles = {
  button: {
    borderColor: theme.color.text.muted,
    borderWidth: 1,
  },
  copy: {
    color: theme.color.text.muted,
  },
};

const onDarkStyles: ComposableStyles = {
  PRIMARY: StyleSheet.create({
    button: {
      backgroundColor: theme.color.interactive.inverse,
    },
    copy: {
      color: theme.color.text.primary,
    },
    focus: {
      backgroundColor: theme.color.interactive.inversePressed,
      borderColor: theme.color.border.focused,
      borderWidth: 1,
    },
    hover: {
      backgroundColor: theme.color.interactive.inverseHovered,
    },
    press: {
      backgroundColor: theme.color.interactive.inversePressed,
    },
  }),
  SECONDARY: StyleSheet.create({
    ...secondaryOnDarkSharedStyles,
  }),
  SECONDARY_ALT: StyleSheet.create({
    ...secondaryOnDarkSharedStyles,
  }),
  TEXT: StyleSheet.create({
    button: {
      backgroundColor: 'transparent',
    },
    copy: {
      color: theme.color.text.inverse,
    },
    focus: {
      borderColor: theme.color.border.focusedInverse,
      borderWidth: 1,
    },
  }),
};

const disabledOnDarkStyles: ComposableStyles = {
  PRIMARY: StyleSheet.create({
    button: {
      backgroundColor: '#ffffff66',
    },
    copy: {
      color: theme.color.interactive.disabled.text,
    },
  }),
  SECONDARY: StyleSheet.create({
    ...secondaryDisabledOnDarkSharedStyles,
  }),
  SECONDARY_ALT: StyleSheet.create({
    ...secondaryDisabledOnDarkSharedStyles,
  }),
  TEXT: StyleSheet.create({
    // eslint-disable-next-line react-native/no-unused-styles
    copy: {
      color: theme.color.interactive.disabled.text,
    },
  }),
};

const baseStyles = StyleSheet.create({
  button: {
    alignItems: 'center',
    borderRadius: 4,
    justifyContent: 'center',
    paddingHorizontal: 12,
  },
  copy: {
    fontFamily: VaroFontNames.NeueHaasGroteskDisplayProMediumNormal,
    fontSize: 14,
    letterSpacing: 0.02,
  },
  full: {
    width: '100%',
  },
  hug: {
    width: 'auto',
  },
  large: {
    minHeight: 40,
    paddingVertical: theme.spacing.s,
  },
  small: {
    minHeight: 32,
    paddingVertical: theme.spacing.xs,
  },
});

export default Button;
